import { useEffect, useState } from 'react'
import { useNavigate, NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getLocations } from '../shared/api/locations.js'
import { getCities } from '../shared/api/cities.js'
import { ExternalLinkIcon } from '../shared/ui/icons.js'
import { useAuthStore } from '../shared/store/auth.store.js'
import { useOrgStore } from '../shared/store/org.store.js'
import { ROUTES } from './routes.js'
import type { MyOrganizationResponse } from '../shared/types/models.js'
import { canViewCustomers, isPlatformAdminRole } from '../shared/utils/permissions.js'

interface NavItem {
  to: string
  label: string
  end: boolean
}

export default function AppLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const user = useAuthStore(s => s.user)
  const logout = useAuthStore(s => s.logout)
  const organizations = useOrgStore(s => s.organizations)
  const activeOrganization = useOrgStore(s => s.activeOrganization)
  const setActive = useOrgStore(s => s.setActive)

  const activeOrg: MyOrganizationResponse | undefined = activeOrganization ?? organizations[0]

  // Hooks must run unconditionally: the organization is resolved asynchronously,
  // so the early return below must never change the hook order between renders.
  // Public storefront URL for the active organization: the SPA route is
  // /:citySlug/:orgSlug(/:locationSlug) and resolves by org slug; the city
  // segment comes from the location that actually belongs to a city.
  const [siteUrl, setSiteUrl] = useState<string | null>(null)
  const [siteChecked, setSiteChecked] = useState(false)
  const [siteReason, setSiteReason] = useState<'ok' | 'noLocations' | 'noCity'>('noLocations')

  const activeOrgId = activeOrg?.id
  const activeOrgSlug = activeOrg?.slug

  useEffect(() => {
    let cancelled = false
    setSiteUrl(null)
    setSiteChecked(false)
    if (!activeOrgId || !activeOrgSlug) {
      return () => {
        cancelled = true
      }
    }
    ;(async () => {
      try {
        const [locs, cities] = await Promise.all([getLocations(activeOrgId), getCities()])
        if (cancelled) return
        // Deterministic city choice: the city holding the most ACTIVE locations,
        // ties broken by slug (never by API order). Locations without a city are
        // ignored — no fabricated URL segments.
        const activeLocations = locs.data.filter(location => location.status === 'ACTIVE')
        const counts = new Map<string, { slug: string; count: number }>()
        for (const location of activeLocations) {
          if (!location.cityId) continue
          const city = cities.find(c => c.id === location.cityId && c.status === 'ACTIVE')
          if (!city) continue
          const prev = counts.get(city.id)
          if (prev) prev.count += 1
          else counts.set(city.id, { slug: city.slug, count: 1 })
        }
        if (activeLocations.length === 0) {
          setSiteReason('noLocations')
        } else if (counts.size === 0) {
          setSiteReason('noCity')
        } else {
          const chosen = [...counts.values()].sort(
            (a, b) => b.count - a.count || a.slug.localeCompare(b.slug),
          )[0]!
          setSiteUrl(`/${encodeURIComponent(chosen.slug)}/${encodeURIComponent(activeOrgSlug)}`)
          setSiteReason('ok')
        }
      } catch {
        // Leave the button in the "unavailable" state; never open a guessed URL.
        setSiteReason('noLocations')
      }
      if (!cancelled) setSiteChecked(true)
    })()
    return () => {
      cancelled = true
    }
  }, [activeOrgId, activeOrgSlug])

  if (!activeOrg) return null

  const displayName = user?.firstName || user?.name || user?.email || ''

  const navItems: NavItem[] = [
    { to: ROUTES.OVERVIEW, label: t('app.overview'), end: true },
    { to: ROUTES.ORDERS, label: t('app.orders'), end: true },
    { to: ROUTES.LOCATIONS, label: t('app.locations'), end: true },
    { to: ROUTES.MENU, label: t('app.menu'), end: true },
    { to: ROUTES.PRODUCTS, label: t('app.products'), end: true },
    ...(canViewCustomers(activeOrg.role)
      ? [{ to: ROUTES.CUSTOMERS, label: t('app.customers'), end: true }]
      : []),
    { to: ROUTES.SETTINGS, label: t('app.settings'), end: true },
  ]

  async function handleLogout() {
    await logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="app-header-left">
          <span className="app-logo">Magic Food</span>
          {renderOrgSwitcher(activeOrg, organizations, setActive)}
        </div>
        <div className="app-header-right">
          {siteUrl ? (
            <a
              className="btn btn-ghost app-site-link"
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('app.goToSiteAria', { name: activeOrg.name })}
              title={t('app.goToSite')}
            >
              <ExternalLinkIcon size={14} />
              <span className="app-site-link-label">{t('app.goToSite')}</span>
            </a>
          ) : (
            <button
              type="button"
              className="btn btn-ghost app-site-link"
              disabled
              title={
                !siteChecked
                  ? t('app.siteLoading')
                  : siteReason === 'noCity'
                    ? t('app.siteNoCityHint')
                    : t('app.siteNoLocations')
              }
              aria-label={t('app.goToSite')}
            >
              <ExternalLinkIcon size={14} />
              <span className="app-site-link-label">{t('app.goToSite')}</span>
            </button>
          )}
          <span className="app-user">{displayName}</span>
          {isPlatformAdminRole(user?.platformRole) && (
            <NavLink className="btn btn-ghost" to={ROUTES.PLATFORM}>
              {t('app.platform')}
            </NavLink>
          )}
          <button className="btn btn-ghost" onClick={handleLogout}>
            {t('app.logout')}
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav className="app-sidebar" aria-label={t('app.dashboard')}>
          <div className="sidebar-section-label">{t('app.dashboard')}</div>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive ? 'sidebar-link sidebar-link--active' : 'sidebar-link'}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="app-main">
          <Outlet key={activeOrg.id} />
        </main>
      </div>
    </div>
  )
}

function renderOrgSwitcher(
  org: MyOrganizationResponse,
  orgs: MyOrganizationResponse[],
  setActive: (org: MyOrganizationResponse) => void,
) {
  if (orgs.length <= 1) {
    return <span className="org-badge">{org.name}</span>
  }
  return (
    <div className="org-switcher">
      <select
        onChange={e => {
          const next = orgs.find(o => o.id === (e.target as HTMLSelectElement).value)
          if (next) setActive(next)
        }}
        value={org.id}
      >
        {orgs.map(o => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  )
}